import React, { useState, useEffect } from 'react';
import { CalendarDays, Clipboard, FileText, Activity, Search, User, BarChart2, Clock, ThumbsUp, AlertTriangle } from 'lucide-react';
import { useParams, useLocation } from 'react-router-dom';
import { usePatientContext } from './PatientContext';

// Sample data structure based on questionnaire output
const samplePatientData = {
  patient: {
    id: "PT100428",
    name: "John Doe",
    dob: "1985-06-15",
    age: "37",
    gender: "Male",
    phone: "(555) 123-4567",
    email: "john.doe@example.com",
    insuranceProvider: "Blue Cross",
    policyNumber: "BCBS1234567"
  },
  visit: {
    id: "VT20250502-001",
    date: "2025-05-02",
    time: "10:30 AM",
    provider: "Dr. Sarah Johnson",
    type: "Urgent Care Visit",
    reason: "Acute respiratory symptoms"
  },
  questionnaire: {
    hpi: [
      "Patient presents with 3-day history of productive cough, fever, and nasal congestion.",
      "Symptoms began gradually after exposure to sick coworker.",
      "Reports fever up to 101.2°F, measured at home.",
      "Cough produces yellow-green sputum, worse in the mornings.",
      "Complains of moderate sinus pressure and occasional headache.",
      "Tried OTC cold medicine with minimal relief."
    ],
    ros: [
      "Respiratory: Productive cough, shortness of breath with exertion",
      "ENT: Nasal congestion, sinus pressure, sore throat",
      "Constitutional: Fever, fatigue, decreased appetite",
      "Cardiovascular: Denies chest pain or palpitations",
      "Gastrointestinal: Mild nausea, no vomiting or diarrhea",
      "Musculoskeletal: Mild body aches",
      "Neurological: Occasional headache, no dizziness"
    ],
    assessment_plan: [
      "Assessment: Acute bronchitis (J20.9)",
      "Viral etiology most likely based on presentation and exam findings",
      "Low risk for pneumonia given normal vital signs and clear lung bases",
      "",
      "Plan:",
      "1. Symptomatic treatment with guaifenesin 600mg q12h for 7 days",
      "2. Increase fluid intake and rest",
      "3. Follow up in 5-7 days if symptoms worsen or fail to improve",
      "4. Return immediately if develops high fever, severe shortness of breath, or chest pain"
    ],
    medications: "Multivitamin daily, Loratadine 10mg PRN for seasonal allergies",
    allergies: "NKDA",
    suggested_tests: ["Rapid COVID-19", "Rapid influenza"]
  },
  vitals: {
    temperature: "100.4°F",
    heartRate: "88",
    respiratoryRate: "18",
    bloodPressure: "124/78",
    oxygenSaturation: "97%"
  },
  labResults: [
    { name: "Rapid COVID-19", result: "Negative", date: "2025-05-02" },
    { name: "Rapid Influenza", result: "Negative", date: "2025-05-02" }
  ],
  icdCodes: [
    { code: "J20.9", description: "Acute bronchitis, unspecified" },
    { code: "J00", description: "Acute nasopharyngitis [common cold]" }
  ]
};

const ElectronicMedicalRecord = ({ patientData }) => {
    const [activeTab, setActiveTab] = useState('hpi');
    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [alertShown, setAlertShown] = useState(true);
    const params = useParams();
    const location = useLocation();

    const { emrPatientData, setEmrPatientData } = usePatientContext();

    const clearEMRData = () => {
        localStorage.removeItem('emr-patient-data');
    };

    // Update the ElectronicMedicalRecord component's useEffect for better handling of updates
    useEffect(() => {
      // Function to handle localStorage updates
      const handleStorageUpdate = () => {
        const storedData = localStorage.getItem('emr-patient-data');
        if (storedData) {
          try {
            const patientData = JSON.parse(storedData);
            console.log('Received patient data from localStorage:', patientData);
            setPatient(patientData);
            setLoading(false);
          } catch (error) {
            console.error('Error parsing patient data from localStorage:', error);
          }
        }
      };

      // Listen for storage events (fired when localStorage is updated from another window)
      window.addEventListener('storage', handleStorageUpdate);
      
      // For same-window updates, use a custom event
      const handleEmrUpdate = (event) => {
        console.log('Custom emr-patient-update event received');
        if (event.detail && event.detail.patient) {
          console.log('Patient data from custom event:', event.detail.patient);
          setPatient(event.detail.patient);
          setLoading(false);
        } else {
          // If no detail in event, try reading from localStorage
          handleStorageUpdate();
        }
      };
      
      window.addEventListener('emr-patient-update', handleEmrUpdate);

      // Check if there's already data in localStorage on mount
      handleStorageUpdate();

      return () => {
        window.removeEventListener('storage', handleStorageUpdate);
        window.removeEventListener('emr-patient-update', handleEmrUpdate);
      };
    }, []);
      
      // Also add a custom event listener as a fallback
      useEffect(() => {
        const handleEmrUpdate = (event) => {
          const { patient: newPatient } = event.detail;
          console.log('Received patient data via custom event:', newPatient);
          setPatient(newPatient);
        };
        
        window.addEventListener('emr-patient-update', handleEmrUpdate);
        
        return () => {
          window.removeEventListener('emr-patient-update', handleEmrUpdate);
        };
      }, []);

      const calculateAge = (dob) => {
        if (!dob || !isValidDOB(dob)) return null;
      
        let birthDate;
        if (dob.includes('/')) {
          const [month, day, year] = dob.split('/');
          birthDate = new Date(`${year}-${month}-${day}`);
        } else if (dob.includes('-')) {
          const [year, month, day] = dob.split('-');
          birthDate = new Date(`${year}-${month}-${day}`);
        } else {
          return null;
        }
      
        if (isNaN(birthDate)) return null;
      
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
      
        return age;
      };


    // In ElectronicMedicalRecord.jsx - add console logs to useEffect

    /*useEffect(() => {
        // Load patient data from props, context, location state, or API
        const loadPatientData = async () => {
          setLoading(true);
          
          try {
            console.log("Loading patient data with:", { 
              hasPatientDataProp: !!patientData, 
              hasContextData: !!emrPatientData,
              locationState: location.state,
              patientIdParam: params.patientId 
            });
            
            // First try context data (highest priority)
            if (emrPatientData) {
              console.log("Using patient data from context");
              setPatient(emrPatientData);
            }
            // Then try props
            else if (patientData) {
              console.log("Using patient data from props");
              setPatient(patientData);
            } 
            // Then try location state
            else if (location.state?.patient) {
              console.log("Using patient data from location state");
              setPatient(location.state.patient);
            } 
            // Then try API
            else if (params.patientId) {
              console.log("Fetching patient data from API");
              const response = await fetch(`/api/patients/${params.patientId}`);
              if (!response.ok) {
                throw new Error('Failed to load patient data');
              }
              const data = await response.json();
              setPatient(data);
            } else {
              // Fallback to sample data if no patient info is available
              console.log("Using sample patient data as fallback");
              setPatient(samplePatientData);
            }
          } catch (err) {
            console.error('Error loading patient data:', err);
            setError('Failed to load patient data');
            // Still use sample data as fallback
            setPatient(samplePatientData);
          } finally {
            setLoading(false);
          }
        };
    
        loadPatientData();
      }, [patientData, emrPatientData, params.patientId, location.state]); */

      if (loading) {
        return <div className="flex justify-center items-center h-screen">Loading patient data...</div>;
      }
    
      if (error) {
        return <div className="text-red-500 p-4">Error: {error}</div>;
      }

  // Early return if no patient data is available
  if (!patient) {
    return <div className="flex justify-center items-center h-screen">No patient data available</div>;
  }

  return (
    <div className="flex flex-col h-full bg-gray-100 rounded-lg shadow-lg overflow-hidden">
      {/* Top Bar */}
      <div className="bg-blue-800 text-white py-2 px-4 flex justify-between items-center">
        <div className="flex items-center">
          {/*<span className="font-bold text-xl">Preface MediChart</span>*/}
          <span className="text-xs bg-blue-700 rounded px-2 py-1 ml-2">Sample EMR v4.2</span>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <input 
              type="text" 
              placeholder="Search patients..." 
              className="rounded-full py-1 px-4 pr-8 text-sm text-gray-800 focus:outline-none"
            />
            <Search className="absolute right-2 top-1.5 h-4 w-4 text-gray-500" />
          </div>
          <User className="h-5 w-5" />
        </div>
      </div>
      
      {/* Alert Banner */}
      {alertShown && (
        <div className="bg-green-300 border-l-4 border-green-500 p-10 flex justify-center items-center">
          <div className="flex items-center">
            {/*<AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />*/}
            <span className="text-3xl text-black">
              <strong>Preface Clinical AI sits on top of your <u>existing EMR.</u></strong> This is a simulation EMR to help visualize your EMR!
            </span>
          </div>
          {/*<button 
            onClick={() => setAlertShown(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            ×
          </button> */}
        </div>
      )}

      {/* Patient Info Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex justify-between">
        <div className="flex items-center space-x-6">
          <div>
            <h2 className="text-xl font-bold">{patient.patient?.name || 'Unknown Patient'}</h2>
            <div className="flex text-sm text-gray-600 space-x-4">
              {/*<span>{patient.patient?.gender || 'Unknown'} • {patient.patient?.age || '?'}y</span>
              {patient.patient?.dob && (
                <span>DOB: {patient.patient.dob}</span>
                )*/}
              <span>MRN: {patient.patient?.id || 'Unknown'}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
            Visit: {patient.visit?.date || 'Current'}
          </span>
          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium">
            {patient.visit?.type || 'Office Visit'}
          </span>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-grow min-h-0">
        {/* Left Sidebar */}
        <div className="w-20 bg-gray-800 text-white flex flex-col items-center py-4">
          <button className="p-3 rounded-lg mb-4 hover:bg-gray-700">
            <User className="h-6 w-6" />
          </button>
          <button className="p-3 rounded-lg mb-4 hover:bg-gray-700">
            <CalendarDays className="h-6 w-6" />
          </button>
          <button className="p-3 rounded-lg mb-4 hover:bg-gray-700">
            <Clipboard className="h-6 w-6" />
          </button>
          <button className="p-3 rounded-lg mb-4 hover:bg-gray-700">
            <FileText className="h-6 w-6" />
          </button>
          <button className="p-3 rounded-lg mb-4 hover:bg-gray-700">
            <Activity className="h-6 w-6" />
          </button>
          <button className="p-3 rounded-lg mb-4 hover:bg-gray-700">
            <BarChart2 className="h-6 w-6" />
          </button>
        </div>

        {/* Right Content */}
        <div className="flex-grow flex flex-col overflow-hidden">
          {/* Tab Navigation */}
          <div className="bg-white border-b border-gray-200">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('hpi')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'hpi'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                HPI & Vitals
              </button>
              <button
                onClick={() => setActiveTab('ros')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'ros'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Review of Systems
              </button>
              <button
                onClick={() => setActiveTab('physical')}
                className={`px-6 py-3 text-sm font-medium ${
                    activeTab === 'physical'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                >
                Physical Exam
                </button>
              <button
                onClick={() => setActiveTab('assessment')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'assessment'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Assessment & Plan
              </button>
              <button
                onClick={() => setActiveTab('tests')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'tests'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Tests & Results
              </button>
              {/*<button
                onClick={() => setActiveTab('codes')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'codes'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Codes & Billing
              </button>*/}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="flex-grow p-6 overflow-auto bg-white">
            {activeTab === 'hpi' && (
              <div>
                <div className="flex mb-6">
                  <div className="w-3/4 pr-6">
                    <h2 className="text-lg font-bold mb-3 text-gray-800 flex items-center">
                      <Clock className="h-5 w-5 mr-2 text-blue-600" />
                      History of Present Illness
                    </h2>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      {patient.questionnaire?.hpi?.map((item, index) => (
                        <p key={index} className="mb-2">{item}</p>
                      )) || <p>No HPI data available</p>}
                    </div>
                    
                    <h3 className="text-md font-bold mt-6 mb-3 text-gray-800 flex items-center">
                      <ThumbsUp className="h-5 w-5 mr-2 text-blue-600" />
                      Current Medications
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p>{patient.questionnaire?.medications || 'No medication data available'}</p>
                    </div>
                    
                    <h3 className="text-md font-bold mt-6 mb-3 text-gray-800 flex items-center">
                      <AlertTriangle className="h-5 w-5 mr-2 text-red-500" />
                      Allergies
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p>{patient.questionnaire?.allergies || 'NKDA'}</p>
                    </div>
                  </div>
                  
                  <div className="w-1/4">
                    <h2 className="text-lg font-bold mb-3 text-gray-800 flex items-center">
                      <Activity className="h-5 w-5 mr-2 text-blue-600" />
                      Vital Signs
                    </h2>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <div className="mb-2">
                        <span className="text-gray-600 text-sm">Temperature:</span>
                        <span className="font-medium ml-2">{patient.vitals?.temperature || 'N/A'}</span>
                      </div>
                      <div className="mb-2">
                        <span className="text-gray-600 text-sm">Heart Rate:</span>
                        <span className="font-medium ml-2">{patient.vitals?.heartRate || 'N/A'} bpm</span>
                      </div>
                      <div className="mb-2">
                        <span className="text-gray-600 text-sm">Resp. Rate:</span>
                        <span className="font-medium ml-2">{patient.vitals?.respiratoryRate || 'N/A'} /min</span>
                      </div>
                      <div className="mb-2">
                        <span className="text-gray-600 text-sm">BP:</span>
                        <span className="font-medium ml-2">{patient.vitals?.bloodPressure || 'N/A'} mmHg</span>
                      </div>
                      <div className="mb-2">
                        <span className="text-gray-600 text-sm">O₂ Sat:</span>
                        <span className="font-medium ml-2">{patient.vitals?.oxygenSaturation || 'N/A'}</span>
                      </div>
                    </div>
                    
                    <h3 className="text-md font-bold mt-6 mb-3 text-gray-800">Visit Info</h3>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <div className="mb-2">
                        <span className="text-gray-600 text-sm">Date:</span>
                        <span className="font-medium ml-2">{patient.visit?.date || 'N/A'}</span>
                      </div>
                      <div className="mb-2">
                        <span className="text-gray-600 text-sm">Time:</span>
                        <span className="font-medium ml-2">{patient.visit?.time || 'N/A'}</span>
                      </div>
                      <div className="mb-2">
                        <span className="text-gray-600 text-sm">Visit Type:</span>
                        <span className="font-medium ml-2">{patient.visit?.type || 'N/A'}</span>
                      </div>
                      <div className="mb-2">
                        <span className="text-gray-600 text-sm">Provider:</span>
                        <span className="font-medium ml-2">{patient.visit?.provider || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'ros' && (
              <div>
                <h2 className="text-lg font-bold mb-4 text-gray-800">Review of Systems</h2>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  {patient.questionnaire?.ros?.map((item, index) => {
                    // Split system name from findings
                    const parts = item.split(':');
                    const system = parts.length > 0 ? parts[0] : '';
                    const findings = parts.length > 1 ? parts[1] : '';
                    
                    return (
                      <div key={index} className="mb-3">
                        <span className="font-bold text-gray-700">{system}:</span>
                        <span className="ml-2">{findings}</span>
                      </div>
                    );
                  }) || <p>No ROS data available</p>}
                </div>
              </div>
            )}

            {activeTab === 'assessment' && (
            <div>
                <h2 className="text-lg font-bold mb-4 text-gray-800">Assessment & Plan</h2>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                {patient.questionnaire?.assessment_plan?.map((item, index) => {
                    // Special formatting for Assessment and Plan headers
                    if (item.startsWith('Assessment:')) {
                    return (
                        <h3 key={index} className="font-bold text-md text-gray-800 mb-2">{item}</h3>
                    );
                    } else if (item === "Plan:") {
                    return (
                        <h3 key={index} className="font-bold text-md text-gray-800 mt-4 mb-2">{item}</h3>
                    );
                    } else if (item.match(/^\d\./)) { // Numbered plan items
                    return (
                        <div key={index} className="mb-2 pl-4">{item}</div>
                    );
                    } else if (item === "") { // Empty line
                    return <div key={index} className="h-2"></div>;
                    } else {
                    return <div key={index} className="mb-2">{item}</div>;
                    }
                }) || <p>No assessment & plan data available</p>}
                </div>
                
                {/* ICD-10 Diagnosis Codes */}
                <h3 className="text-md font-bold mt-6 mb-2 text-gray-800">ICD-10 Diagnosis Codes</h3>
                <div className="overflow-hidden border border-gray-200 rounded-lg mb-6">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                        </th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {patient.icdCodes?.map((code, index) => (
                        <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{code.code}</div>
                        </td>
                        <td className="px-6 py-4">
                            <div className="text-sm text-gray-700">{code.description}</div>
                        </td>
                        </tr>
                    )) || (
                        <tr>
                        <td colSpan="2" className="px-6 py-4 text-center text-gray-500">
                            No ICD codes available
                        </td>
                        </tr>
                    )}
                    </tbody>
                </table>
                </div>
                
                {/* Suggested Tests */}
                <h3 className="text-md font-bold mt-4 mb-2 text-gray-800">Diagnoses</h3>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <ul className="list-disc pl-5">
                    {patient.questionnaire?.suggested_tests?.map((test, index) => (
                    <li key={index}>{test}</li>
                    )) || <li>No diagnoses</li>}
                </ul>
                </div>
            </div>
            )}

            {activeTab === 'physical' && (
            <div>
                <h2 className="text-lg font-bold mb-4 text-gray-800">Physical Exam</h2>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                {patient.questionnaire?.physical_exam?.map((item, index) => (
                    <p key={index} className="mb-2">{item}</p>
                )) || <p>No physical exam data available</p>}
                </div>
            </div>
            )}

            {activeTab === 'tests' && (
              <div>
                <h2 className="text-lg font-bold mb-4 text-gray-800">Tests & Results</h2>
                
                <div className="overflow-hidden border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Test Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Result
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {patient.labResults?.map((result, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{result.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm ${
                              result.result === "Negative" 
                                ? "text-green-700 font-medium" 
                                : "text-red-700 font-medium"
                            }`}>
                              {result.result}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{result.date}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Completed
                            </span>
                          </td>
                        </tr>
                      )) || (
                        <tr>
                          <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                            No lab results available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-6">
                  <button className="bg-blue-600 text-white px-4 py-2 rounded font-medium hover:bg-blue-700 flex items-center">
                    <span className="mr-2">Order New Test</span>
                    <span className="text-xl">+</span>
                  </button>
                </div>
              </div>
            )}

            {/*activeTab === 'codes' && (
                <div>
                <h2 className="text-lg font-bold mb-4 text-gray-800">Codes & Billing</h2>
                
                <h3 className="text-md font-bold mb-2 text-gray-800">ICD-10 Diagnosis Codes</h3>
                <div className="overflow-hidden border border-gray-200 rounded-lg mb-6">
                    <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                        </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {patient.icdCodes?.map((code, index) => (
                        <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{code.code}</div>
                            </td>
                            <td className="px-6 py-4">
                            <div className="text-sm text-gray-700">{code.description}</div>
                            </td>
                        </tr>
                        )) || (
                        <tr>
                            <td colSpan="2" className="px-6 py-4 text-center text-gray-500">
                            No ICD codes available
                            </td>
                        </tr>
                        )}
                    </tbody>
                    </table>
                </div>
            
                <h3 className="text-md font-bold mb-2 text-gray-800">Billing Summary</h3>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="text-sm text-gray-600">Insurance Provider:</label>
                        <p className="font-medium">{patient.patient?.insuranceProvider || 'Not provided'}</p>
                    </div>
                    <div>
                        <label className="text-sm text-gray-600">Policy Number:</label>
                        <p className="font-medium">{patient.patient?.policyNumber || 'Not provided'}</p>
                    </div>
                    <div>
                        <label className="text-sm text-gray-600">Visit Type:</label>
                        <p className="font-medium">{patient.visit?.type || 'Standard Office Visit'}</p>
                    </div>
                    </div>
                </div>
                </div>
            )*/}
            </div>

          {/* Footer */}
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Last updated: May 2, 2025 at 11:45 AM
            </div>
            <div className="text-sm">
              <button className="text-blue-600 hover:text-blue-800 font-medium">
                Print Note
              </button>
              <span className="mx-2 text-gray-300">|</span>
              <button className="text-blue-600 hover:text-blue-800 font-medium">
                Export PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElectronicMedicalRecord;